import { useEffect, useState, type ReactNode } from 'react';
import { cx } from '../lib/cx';

/**
 * The dense field primitives every editing surface in this app is built from.
 *
 * ── Why they are here rather than in a feature folder ───────────────────────
 * There were two identical copies — `features/requests/RequestFields.tsx` and
 * `features/settings/fields.tsx` — right down to the same 130-character
 * Tailwind string and the same blur/Enter/Escape commit semantics. The
 * settings copy carried a note admitting it: "Deliberately local to this
 * feature … requests/ is outside this milestone's file-ownership boundary
 * regardless. Re-implemented here at the same visual weight so the two screens
 * read as one product rather than two."
 *
 * Two screens reading as one product is exactly what a shared component is
 * for. Keeping them apart meant the next change to the commit semantics — and
 * there will be one, because "when does a field save" is never settled the
 * first time — would land on one screen and not the other.
 */

/**
 * 13px controls on a hairline border, matching the prototype's density.
 *
 * Exported because a handful of places need the same look on an input this
 * file does not wrap (a date picker, a textarea). Prefer the components.
 */
export const CONTROL_CLASS =
  'min-w-0 rounded-control border border-line bg-base px-[0.4375rem] py-[0.3125rem] fs-12.5 text-ink ' +
  'focus:border-line-heavy disabled:opacity-50';

/** A label above its control, as one `<label>` so the association is structural. */
export function FieldShell({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cx('flex min-w-0 flex-col gap-1', className)}>
      <span className="fs-11 whitespace-nowrap text-muted">{label}</span>
      {children}
    </label>
  );
}

/**
 * A value that cannot be edited here, at the same weight as one that can.
 *
 * `hint` is for the field whose read-only-ness is the first thing a reader
 * questions — a derived value. Saying what it is derived from, right under it,
 * is the difference between "this is computed" and "this is broken".
 */
export function ReadOnlyField({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <FieldShell label={label}>
      <span className="truncate py-[0.3125rem] fs-12.5 font-semibold text-ink">{value}</span>
      {hint !== undefined && <span className="-mt-0.5 truncate fs-10.5 text-faint">{hint}</span>}
    </FieldShell>
  );
}

export function SelectField<T extends string>({
  label,
  value,
  options,
  labelOf,
  disabled,
  onChange,
  placeholder,
}: {
  label: string;
  value: T | '';
  options: readonly T[];
  labelOf: (option: T) => string;
  disabled?: boolean;
  onChange: (next: T) => void;
  /** Rendered as an empty-valued first option, for a field that may be unset. */
  placeholder?: string;
}) {
  return (
    <FieldShell label={label}>
      <select
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value as T)}
        className={CONTROL_CLASS}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option} value={option}>
            {labelOf(option)}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

/**
 * A text or number input that commits on blur and on Enter, never per
 * keystroke.
 *
 * Two behaviours worth keeping together, because they are the reason this is a
 * component rather than an `<input>`:
 *
 *  - **Saving per keystroke would write an activity row per character.** The
 *    server records a trail entry per changed field, so a field that saved as
 *    you typed would fill the audit trail with one `config.changed` row per key
 *    pressed.
 *  - **The draft re-seeds from `value` only while the field is NOT focused.**
 *    Without that, a realtime update from another admin's tab yanks text out
 *    from under someone mid-sentence.
 *
 * Escape reverts and blurs, which is the only way out that does not save.
 */
export function CommittedField({
  label,
  value,
  type = 'text',
  placeholder,
  disabled,
  onCommit,
}: {
  label: string;
  value: string;
  type?: 'text' | 'number';
  placeholder?: string;
  disabled?: boolean;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    if (draft === value) return;
    onCommit(draft);
  };

  return (
    <FieldShell label={label}>
      <input
        type={type}
        value={draft}
        disabled={disabled}
        placeholder={placeholder}
        min={type === 'number' ? 0 : undefined}
        onFocus={() => setEditing(true)}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.blur();
          }
          if (event.key === 'Escape') {
            setDraft(value);
            setEditing(false);
            event.currentTarget.blur();
          }
        }}
        className={CONTROL_CLASS}
      />
    </FieldShell>
  );
}

/** "Saving…" and the error from the last attempt, in a fixed-height row. */
export function InlineStatus({ pending, error }: { pending: boolean; error: string | null }) {
  if (!pending && error === null) return null;
  return (
    <div className="mt-2 flex min-h-[1rem] items-center gap-2 fs-11">
      {pending && <span className="text-muted">Saving…</span>}
      {error !== null && (
        <span className="text-danger" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
